import type { OrganizationSettings, UpdateOrganizationInput } from '@chukua/common';
import { Alert, Button, Form } from 'antd';
import { Briefcase, Building2, Globe, MapPin, Truck } from 'lucide-react';
import type { ReactNode } from 'react';
import { DriversFields, JobsFields, LocaleFields, LocationFields, ParticularsFields, type CompanySettingsFormValues } from './CompanySettingsFields';
import { COMPANY_PAGE_GRID } from '../policies/company-page-layout';
import { SettingsCard } from './SettingsCard';

interface CompanySettingsFormProps {
  settings: OrganizationSettings;
  error: string | null;
  submitting: boolean;
  onSave: (input: UpdateOrganizationInput) => Promise<boolean>;
  /** The branding card: it saves on its own (each logo uploads as picked), so it sits beside the form's cards. */
  aside: ReactNode;
}

function initialValuesOf(settings: OrganizationSettings): CompanySettingsFormValues {
  return {
    name: settings.name,
    legalName: settings.legalName ?? undefined,
    kraPin: settings.kraPin ?? undefined,
    address: settings.address ?? undefined,
    location: settings.location,
    currency: settings.currency,
    timezone: settings.timezone,
    dateFormat: settings.dateFormat,
    language: settings.language,
    jobDueSoonThresholdDays: settings.jobDueSoonThresholdDays,
    creditOverdueThresholdDays: settings.creditOverdueThresholdDays,
    driverRestHours: settings.driverRestHours,
  };
}

/**
 * The company page as one form in two columns, so it fits a screen rather than scrolling: particulars and
 * locale with the save on the left, the location map and the branding on the right. One save covers every
 * card but branding, whose logos upload as they are picked. Stacked on narrower screens.
 */
export function CompanySettingsForm({ settings, error, submitting, onSave, aside }: CompanySettingsFormProps) {
  const [form] = Form.useForm<CompanySettingsFormValues>();

  async function submit(values: CompanySettingsFormValues) {
    await onSave({
      name: values.name,
      legalName: values.legalName || undefined,
      kraPin: values.kraPin || undefined,
      address: values.address || undefined,
      location: values.location ?? null,
      currency: values.currency,
      timezone: values.timezone,
      dateFormat: values.dateFormat,
      language: values.language,
      jobDueSoonThresholdDays: values.jobDueSoonThresholdDays,
      creditOverdueThresholdDays: values.creditOverdueThresholdDays,
      driverRestHours: values.driverRestHours,
    });
  }

  return (
    <Form<CompanySettingsFormValues>
      form={form}
      layout="vertical"
      requiredMark={false}
      disabled={submitting}
      onFinish={submit}
      initialValues={initialValuesOf(settings)}
      className={COMPANY_PAGE_GRID}
    >
      <div className="flex min-w-0 flex-col gap-6">
        {error && <Alert type="error" title={error} showIcon />}
        <SettingsCard icon={Building2} title="Company particulars">
          <ParticularsFields />
        </SettingsCard>
        <SettingsCard icon={Globe} title="Locale">
          <LocaleFields />
        </SettingsCard>
        <SettingsCard icon={Briefcase} title="Jobs">
          <JobsFields />
        </SettingsCard>
        <SettingsCard icon={Truck} title="Drivers">
          <DriversFields />
        </SettingsCard>
        <div>
          <Button type="primary" htmlType="submit" loading={submitting}>
            Save changes
          </Button>
        </div>
      </div>
      <div className="flex min-w-0 flex-col gap-6">
        <SettingsCard icon={MapPin} title="Location">
          <LocationFields />
        </SettingsCard>
        {aside}
      </div>
    </Form>
  );
}
